import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import InstallPrompt from './components/InstallPrompt';
import Home from './pages/Home';
// Route-level code splitting: each module downloads only when opened (keeps first load small on phones)
// If a chunk is missing (app updated while open), reload once to pick up the new build instead of a blank page.
const lazyRetry = (importer) => lazy(() => importer().then((m) => { sessionStorage.removeItem('chunk_reload'); return m; }).catch((err) => {
  if (!sessionStorage.getItem('chunk_reload')) {
    sessionStorage.setItem('chunk_reload', '1');
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}));
const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const AdminPanel = lazyRetry(() => import('./pages/AdminPanel'));
const ExpenseVoucher = lazyRetry(() => import('./pages/ExpenseVoucher'));
const MarketingSales = lazyRetry(() => import('./pages/MarketingSales'));
const Sales = lazyRetry(() => import('./pages/Sales'));
const Shortages = lazyRetry(() => import('./pages/Shortages'));
const PaymentApproval = lazyRetry(() => import('./pages/PaymentApproval'));
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'));
const Godown = lazyRetry(() => import('./pages/Godown'));
const PlatesStock = lazyRetry(() => import('./pages/PlatesStock'));
const PortalLogin = lazyRetry(() => import('./pages/PortalLogin'));
const PortalDashboard = lazyRetry(() => import('./pages/PortalDashboard'));
const VehiclePlacementsPage = lazyRetry(() => import('./pages/VehiclePlacementsPage'));
const PortalResetPassword = lazyRetry(() => import('./pages/PortalResetPassword'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="page-loader">
    <div className="flex items-center gap-3 text-slate-500 text-sm"><span className="w-5 h-5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" /> Loading…</div>
  </div>
);
import { Toaster } from './components/ui/sonner';
import { UpdateBanner } from './components/UpdateBanner';
import { PasskeySetupPrompt } from './components/Passkey';
import './App.css';


const userDivisions = () => {
  const u = JSON.parse(localStorage.getItem('user') || 'null');
  if (!u) return [];
  if (u.divisions?.length) return u.divisions;
  return ['admin', 'owner'].includes(u.role) ? ['pig_iron', 'plates'] : ['pig_iron'];
};
// Pig Iron pages are off-limits for Plates-only users (and vice-versa)
const PigIron = ({ children }) => (userDivisions().includes('pig_iron') ? children : <Navigate to="/plates-stock" replace />);
const Plates = ({ children }) => (userDivisions().includes('plates') ? children : <Navigate to="/home" replace />);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/home" /> : <Login setAuth={setIsAuthenticated} />} 
          />
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/reset-password" element={<PortalResetPassword />} />
          <Route 
            path="/placements" 
            element={isAuthenticated ? <PigIron><VehiclePlacementsPage /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/reset-password" 
            element={<ResetPassword />} 
          />
          <Route 
            path="/" 
            element={isAuthenticated ? <Navigate to="/home" /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/home" 
            element={isAuthenticated ? <Home setAuth={setIsAuthenticated} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/dashboard" 
            element={isAuthenticated ? <PigIron><Dashboard setAuth={setIsAuthenticated} /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/plates-stock" 
            element={isAuthenticated ? <Plates><PlatesStock /></Plates> : <Navigate to="/login" />} 
          />
          {/* Redirect /logistics to /dashboard for backward compatibility */}
          <Route 
            path="/logistics" 
            element={<Navigate to="/dashboard" replace />} 
          />
          <Route 
            path="/admin" 
            element={isAuthenticated ? <AdminPanel setAuth={setIsAuthenticated} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/expense" 
            element={isAuthenticated ? <PigIron><ExpenseVoucher /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/marketing" 
            element={isAuthenticated ? <PigIron><MarketingSales /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/sales" 
            element={isAuthenticated ? <PigIron><Sales /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/shortages" 
            element={isAuthenticated ? <PigIron><Shortages /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/payments" 
            element={isAuthenticated ? <PigIron><PaymentApproval /></PigIron> : <Navigate to="/login" />} 
          />
          <Route 
            path="/godown" 
            element={isAuthenticated ? <PigIron><Godown /></PigIron> : <Navigate to="/login" />} 
          />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
      <UpdateBanner />
      <InstallPrompt />
      <PasskeySetupPrompt />
    </>
  );
}

export default App;